import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  TextField,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/FileUpload';
import { CloudUpload } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import { glucoseLogAPI } from '../../services/api';

/** Capillary blood glucose (mg/dL) — typical home-meter usable range; flags implausible OCR/typing errors. */
const GLUCOSE_MIN_MGDL = 20;
const GLUCOSE_MAX_MGDL = 600;

const GLUCOSE_KEYS = [
  'fasting',
  'afterBreakfast',
  'lunchPre',
  'lunchPost',
  'dinnerPre',
  'dinnerPost',
  'threeAM',
] as const;

const emptyManualRow = () => ({
  date: '',
  fasting: '',
  afterBreakfast: '',
  lunchPre: '',
  lunchPost: '',
  dinnerPre: '',
  dinnerPost: '',
  threeAM: '',
});

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoDateYearsAgo(years: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Optional mg/dL field: empty OK; otherwise integer/decimal in clinical range. */
function validateOptionalGlucoseMgDl(raw: string): string | null {
  const t = raw.trim();
  if (t === '') return null;
  const normalized = t.replace(',', '.');
  const n = Number(normalized);
  if (!Number.isFinite(n)) {
    return 'Enter a valid number (mg/dL)';
  }
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) {
    return 'Enter a valid number (mg/dL)';
  }
  if (n < GLUCOSE_MIN_MGDL || n > GLUCOSE_MAX_MGDL) {
    return `Typical range ${GLUCOSE_MIN_MGDL}–${GLUCOSE_MAX_MGDL} mg/dL`;
  }
  return null;
}

function validateManualEntry(row: ReturnType<typeof emptyManualRow>): Record<string, string> {
  const err: Record<string, string> = {};
  const d = row.date.trim();
  if (!d) {
    err.date = 'Log date is required';
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    err.date = 'Choose a valid date';
  } else {
    const parsed = new Date(d + 'T12:00:00');
    if (Number.isNaN(parsed.getTime())) {
      err.date = 'Invalid date';
    } else {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (parsed > today) err.date = 'Date cannot be in the future';
      const min = new Date();
      min.setFullYear(min.getFullYear() - 10);
      if (parsed < min) err.date = 'Date is too far in the past (max ~10 years)';
    }
  }

  let anyReading = false;
  for (const k of GLUCOSE_KEYS) {
    const v = row[k].trim();
    if (v !== '') anyReading = true;
    const msg = validateOptionalGlucoseMgDl(row[k]);
    if (msg) err[k] = msg;
  }
  if (!anyReading) {
    err._form =
      'Enter at least one glucose value for this row (mg/dL), or cancel if you only wanted to set a date';
  }
  return err;
}

const MANUAL_FIELD_LABELS: Record<string, string> = {
  date: 'Date',
  fasting: 'Fasting',
  afterBreakfast: 'After breakfast',
  lunchPre: 'Lunch pre',
  lunchPost: 'Lunch post',
  dinnerPre: 'Dinner pre',
  dinnerPost: 'Dinner post',
  threeAM: '3 AM',
};

const manualTableHeaderSx = {
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: '#424242',
  border: '1px solid #D9E2F2',
  bgcolor: '#F5F7FB',
  py: 1,
  px: 1,
  whiteSpace: 'nowrap' as const,
};

const manualTableCellSx = {
  border: '1px solid #E5EAF2',
  verticalAlign: 'top' as const,
  py: 1,
  px: 0.75,
};

export default function GlucoseLogUpload({
  onUploadComplete,
}: {
  onUploadComplete: (rows: any[]) => void;
}) {
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [useCamera, setUseCamera] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [openManualDialog, setOpenManualDialog] = useState(false);
  const [manualRow, setManualRow] = useState(() => emptyManualRow());
  const [manualErrors, setManualErrors] = useState<Record<string, string>>({});

  const minLogDate = isoDateYearsAgo(10);
  const maxLogDate = todayIsoDate();

  const clearManualFieldError = (key: string) => {
    setManualErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      delete next._form;
      return next;
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Section Title */}
      

      {/* Upload Button */}
      <Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 2,
  }}
>
  <Typography
    variant="subtitle2"
    sx={{
      fontWeight: 600,
      color: '#424242',
    }}
  >
    Glucose Log Upload
  </Typography>

  <Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  }}
>
    
    <Tooltip title="Upload log book image">
  <IconButton
  onClick={() => setOpenUploadDialog(true)}
  size="small"
  sx={{
    ml: 1,
    color: '#FFFFFF',
    bgcolor: '#0D47A1',
    width: 28,
    height: 28,
    '&:hover': {
      bgcolor: '#1565C0',
      color: 'white',
    },
  }}
>
  <UploadIcon sx={{ fontSize: 16 }} />
</IconButton>
</Tooltip>

    <Tooltip title="Add Manually">
      <IconButton
        onClick={() => {
          setManualErrors({});
          setManualRow(emptyManualRow());
          setOpenManualDialog(true);
        }}
        sx={{
          color: '#012676',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EditIcon />
      </IconButton>
    </Tooltip>

  </Box>

  

  
</Box>
      

      {/* Add Manually */}
      

      <Dialog
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Upload Glucose Log</DialogTitle>
        <DialogContent>
          <Paper
            sx={{
              p: 4,
              border: `2px dashed #1976d2`,
              borderRadius: 2,
              mt: 2,
              textAlign: 'center',
              bgcolor: '#fafafa',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': { borderColor: '#1976d2', bgcolor: '#f5f5f5' },
            }}
          >
            <input
              accept="image/*"
              capture={useCamera ? 'environment' : undefined}
              style={{ display: 'none' }}
              id="glucose-log-upload"
              type="file"
              onChange={async (e) => {
                if (e.target.files && e.target.files[0]) {
                  setSelectedFile(e.target.files[0]);
                  try {
                    const response = await glucoseLogAPI.upload(e.target.files[0]);
                    console.log('OCR RESPONSE:', response.data);
                    if (response.data.rows) {
                      onUploadComplete(response.data.rows);
                    }
                  } catch (error) {
                    console.error('Glucose OCR failed:', error);
                  }
                  setOpenUploadDialog(false);
                }
              }}
            />
            <label htmlFor="glucose-log-upload" style={{ cursor: 'pointer' }}>
              <Box>
                <CloudUpload sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
                <Typography variant="h6" gutterBottom>Upload Glucose Logs</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Drag and drop files here, or click to select
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: JPG, PNG, JPEG
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Chip label="Vision AI Enabled" size="small" sx={{ bgcolor: '#009688', color: 'white' }} />
                </Box>
              </Box>
            </label>
          </Paper>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openManualDialog}
        onClose={() => {
          setOpenManualDialog(false);
          setManualRow(emptyManualRow());
          setManualErrors({});
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Add glucose log (manual)</DialogTitle>
        <DialogContent>
          {manualErrors._form ? (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              onClose={() =>
                setManualErrors((prev) => {
                  const next = { ...prev };
                  delete next._form;
                  return next;
                })
              }
            >
              {manualErrors._form}
            </Alert>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Enter one log row like your paper booklet: capillary glucose in <strong>mg/dL</strong> (typical range{' '}
            {GLUCOSE_MIN_MGDL}–{GLUCOSE_MAX_MGDL}). Leave a cell blank if not recorded.
          </Typography>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              mt: 1,
              borderColor: '#D9E2F2',
              borderRadius: 1,
              maxWidth: '100%',
              overflowX: 'auto',
            }}
          >
            <Table size="small" sx={{ minWidth: 880 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ ...manualTableHeaderSx, minWidth: 148 }}>
                    Date
                  </TableCell>
                  <TableCell align="center" sx={{ ...manualTableHeaderSx, minWidth: 88 }}>
                    Fasting
                  </TableCell>
                  <TableCell align="center" sx={{ ...manualTableHeaderSx, minWidth: 104 }}>
                    After breakfast
                  </TableCell>
                  <TableCell align="center" sx={{ ...manualTableHeaderSx, minWidth: 88 }}>
                    Lunch pre
                  </TableCell>
                  <TableCell align="center" sx={{ ...manualTableHeaderSx, minWidth: 92 }}>
                    Lunch post
                  </TableCell>
                  <TableCell align="center" sx={{ ...manualTableHeaderSx, minWidth: 92 }}>
                    Dinner pre
                  </TableCell>
                  <TableCell align="center" sx={{ ...manualTableHeaderSx, minWidth: 96 }}>
                    Dinner post
                  </TableCell>
                  <TableCell align="center" sx={{ ...manualTableHeaderSx, minWidth: 72 }}>
                    3 AM
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ '&:last-of-type td': { borderBottom: '1px solid #E5EAF2' } }}>
                  <TableCell sx={{ ...manualTableCellSx, minWidth: 148 }}>
                    <TextField
                      type="date"
                      size="small"
                      value={manualRow.date}
                      onChange={(e) => {
                        clearManualFieldError('date');
                        setManualRow((r) => ({ ...r, date: e.target.value }));
                      }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: minLogDate, max: maxLogDate }}
                      error={Boolean(manualErrors.date)}
                      fullWidth
                      helperText={manualErrors.date || ' '}
                      FormHelperTextProps={{ sx: { m: 0, minHeight: 20, fontSize: '0.7rem' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...manualTableCellSx, minWidth: 88 }}>
                    <TextField
                      size="small"
                      value={manualRow.fasting}
                      onChange={(e) => {
                        clearManualFieldError('fasting');
                        setManualRow((r) => ({ ...r, fasting: e.target.value }));
                      }}
                      placeholder="mg/dL"
                      inputProps={{
                        inputMode: 'decimal',
                        title: manualErrors.fasting || '',
                      }}
                      error={Boolean(manualErrors.fasting)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ ...manualTableCellSx, minWidth: 104 }}>
                    <TextField
                      size="small"
                      value={manualRow.afterBreakfast}
                      onChange={(e) => {
                        clearManualFieldError('afterBreakfast');
                        setManualRow((r) => ({ ...r, afterBreakfast: e.target.value }));
                      }}
                      placeholder="mg/dL"
                      inputProps={{
                        inputMode: 'decimal',
                        title: manualErrors.afterBreakfast || '',
                      }}
                      error={Boolean(manualErrors.afterBreakfast)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ ...manualTableCellSx, minWidth: 88 }}>
                    <TextField
                      size="small"
                      value={manualRow.lunchPre}
                      onChange={(e) => {
                        clearManualFieldError('lunchPre');
                        setManualRow((r) => ({ ...r, lunchPre: e.target.value }));
                      }}
                      placeholder="mg/dL"
                      inputProps={{
                        inputMode: 'decimal',
                        title: manualErrors.lunchPre || '',
                      }}
                      error={Boolean(manualErrors.lunchPre)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ ...manualTableCellSx, minWidth: 92 }}>
                    <TextField
                      size="small"
                      value={manualRow.lunchPost}
                      onChange={(e) => {
                        clearManualFieldError('lunchPost');
                        setManualRow((r) => ({ ...r, lunchPost: e.target.value }));
                      }}
                      placeholder="mg/dL"
                      inputProps={{
                        inputMode: 'decimal',
                        title: manualErrors.lunchPost || '',
                      }}
                      error={Boolean(manualErrors.lunchPost)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ ...manualTableCellSx, minWidth: 92 }}>
                    <TextField
                      size="small"
                      value={manualRow.dinnerPre}
                      onChange={(e) => {
                        clearManualFieldError('dinnerPre');
                        setManualRow((r) => ({ ...r, dinnerPre: e.target.value }));
                      }}
                      placeholder="mg/dL"
                      inputProps={{
                        inputMode: 'decimal',
                        title: manualErrors.dinnerPre || '',
                      }}
                      error={Boolean(manualErrors.dinnerPre)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ ...manualTableCellSx, minWidth: 96 }}>
                    <TextField
                      size="small"
                      value={manualRow.dinnerPost}
                      onChange={(e) => {
                        clearManualFieldError('dinnerPost');
                        setManualRow((r) => ({ ...r, dinnerPost: e.target.value }));
                      }}
                      placeholder="mg/dL"
                      inputProps={{
                        inputMode: 'decimal',
                        title: manualErrors.dinnerPost || '',
                      }}
                      error={Boolean(manualErrors.dinnerPost)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell sx={{ ...manualTableCellSx, minWidth: 72 }}>
                    <TextField
                      size="small"
                      value={manualRow.threeAM}
                      onChange={(e) => {
                        clearManualFieldError('threeAM');
                        setManualRow((r) => ({ ...r, threeAM: e.target.value }));
                      }}
                      placeholder="mg/dL"
                      inputProps={{
                        inputMode: 'decimal',
                        title: manualErrors.threeAM || '',
                      }}
                      error={Boolean(manualErrors.threeAM)}
                      fullWidth
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {Object.entries(manualErrors).some(([k, v]) => k !== '_form' && k !== 'date' && v) ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0, mt: 1 }}>
              {Object.entries(manualErrors)
                .filter(([k, v]) => k !== '_form' && k !== 'date' && v)
                .map(([key, msg]) => (
                  <Typography
                    key={key}
                    component="li"
                    variant="caption"
                    color="error"
                    sx={{ display: 'list-item' }}
                  >
                    <strong>{MANUAL_FIELD_LABELS[key] ?? key}:</strong> {msg}
                  </Typography>
                ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenManualDialog(false);
              setManualRow(emptyManualRow());
              setManualErrors({});
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const rowForSave = {
                ...manualRow,
                date: manualRow.date.trim(),
                fasting: manualRow.fasting.trim(),
                afterBreakfast: manualRow.afterBreakfast.trim(),
                lunchPre: manualRow.lunchPre.trim(),
                lunchPost: manualRow.lunchPost.trim(),
                dinnerPre: manualRow.dinnerPre.trim(),
                dinnerPost: manualRow.dinnerPost.trim(),
                threeAM: manualRow.threeAM.trim(),
              };
              const errs = validateManualEntry(rowForSave);
              if (Object.keys(errs).length > 0) {
                setManualErrors(errs);
                return;
              }
              onUploadComplete([rowForSave]);
              setOpenManualDialog(false);
              setManualRow(emptyManualRow());
              setManualErrors({});
            }}
          >
            Add row
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}